import { useCallback, useEffect } from 'react';
import { useAtom } from 'jotai';
import {
  accessTokenAtom,
  authErrorAtom,
  isAuthenticatedAtom,
  isInitializedAtom,
  lastFailingEndpointAtom,
  userEmailAtom,
} from '../atoms/authAtom';
import { getBackendTokenWithCache } from '../services/api';
import { isGigyaLoggedIn } from '../services/gigya';

export interface UseAPIReturn {
  token: string | undefined;
  isAuthenticated: boolean;
  error: string | null;
  isInitialized: boolean;
  getAccessToken: () => Promise<string>;
  refreshToken: () => Promise<void>;
  clearAuth: () => void;
}

function getUserEmailFromToken(token?: string): string | null {
  if (!token) {
    return null;
  }

  try {
    const payloadPart = token.split('.')[1];
    if (!payloadPart) {
      return null;
    }

    const normalizedPayload = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
    const paddedPayload = normalizedPayload.padEnd(
      normalizedPayload.length + ((4 - (normalizedPayload.length % 4)) % 4),
      '='
    );

    const payload = JSON.parse(atob(paddedPayload)) as { email?: string };
    return typeof payload.email === 'string' && payload.email.length > 0 ? payload.email : null;
  } catch {
    return null;
  }
}

export const useAPI = (): UseAPIReturn => {
  const [accessToken, setAccessToken] = useAtom(accessTokenAtom);
  const [authError, setAuthError] = useAtom(authErrorAtom);
  const [isAuthenticated, setIsAuthenticated] = useAtom(isAuthenticatedAtom);
  const [isInitialized, setIsInitialized] = useAtom(isInitializedAtom);
  const [, setLastFailingEndpoint] = useAtom(lastFailingEndpointAtom);
  const [, setUserEmail] = useAtom(userEmailAtom);

  // Initialize Gigya SDK and set up token refresh
  useEffect(() => {
    console.debug('[useAPI Hook] Authentication Setup - START');
    console.debug('[useAPI Hook] Component mounted, starting authentication process');
    let isMounted = true;
    let intervalId: number | undefined = undefined;
    let isInitialAuthAttempted = false;

    const refreshAccessToken = async (isInitial = false) => {
      console.debug('[useAPI Hook] refreshAccessToken - START', { isInitial });

      // Prevent multiple simultaneous auth attempts
      // Check global isInitialized flag to prevent double calls from React StrictMode
      if (
        !isMounted ||
        (!isInitial && isInitialAuthAttempted && accessToken) ||
        (isInitial && isInitialized)
      ) {
        console.debug('[useAPI Hook] Skipping auth attempt', {
          isMounted,
          isInitial,
          isInitialAuthAttempted,
          hasToken: !!accessToken,
          isInitialized,
        });
        return;
      }

      try {
        console.debug('[useAPI Hook] refreshAccessToken - Checking Gigya login status...');
        const gigyaLoggedIn = await isGigyaLoggedIn();
        console.debug(
          '[useAPI Hook] refreshAccessToken - Gigya login status result:',
          gigyaLoggedIn
        );

        if (!gigyaLoggedIn) {
          console.warn('[useAPI Hook] refreshAccessToken - User not logged in to Gigya');
          setAuthError('User not logged in to Gigya');
          setIsAuthenticated(false);
          setUserEmail(null);
          if (isInitial && isMounted) {
            setIsInitialized(true);
          }
          return;
        }

        console.debug('[useAPI Hook] refreshAccessToken - Getting cached backend token...');
        const token = await getBackendTokenWithCache();

        if (isMounted) {
          console.debug(
            '[useAPI Hook] refreshAccessToken - JWT received. Updating application state'
          );
          setAccessToken(token);
          setIsAuthenticated(true);
          setAuthError(null);
          setUserEmail(getUserEmailFromToken(token));
          isInitialAuthAttempted = true;

          console.log(
            `[useAPI Hook] refreshAccessToken - Refreshed access token: ${token.substring(0, 15)}...`
          );
        }
      } catch (error) {
        if (isMounted) {
          const errorMessage = error instanceof Error ? error.message : 'Token refresh failed';
          console.error('[useAPI Hook] refreshAccessToken - Token refresh error:', error);
          setAuthError(errorMessage);
          setIsAuthenticated(false);
          setUserEmail(null);
          isInitialAuthAttempted = true;
        }
      } finally {
        if (isInitial && isMounted) {
          setIsInitialized(true);
        }
      }
      console.debug('[useAPI Hook] refreshAccessToken - END');
    };

    // Initial token fetch - only once
    if (!isInitialAuthAttempted) {
      console.debug('[useAPI Hook] Authentication Setup - Performing initial token fetch');
      refreshAccessToken(true);
    }

    // Refresh token every 10 minutes (increased from 5 to reduce frequency)
    console.debug(
      '[useAPI Hook] Authentication Setup - Setting up token refresh interval (10 minutes)'
    );
    intervalId = setInterval(
      () => {
        if (isMounted && isAuthenticated) {
          console.debug('[useAPI Hook] Scheduled token refresh');
          refreshAccessToken(false);
        }
      },
      10 * 60 * 1000 // Changed to 10 minutes
    ) as unknown as number;

    console.debug('[useAPI Hook] Authentication Setup - END');

    return () => {
      console.debug('[useAPI Hook] Cleaning up authentication setup');
      isMounted = false;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array intentional - run only once to avoid multiple auth attempts

  useEffect(() => {
    if (!accessToken) {
      setUserEmail(null);
      return;
    }

    setUserEmail(getUserEmailFromToken(accessToken));
  }, [accessToken, setUserEmail]);

  const getAccessToken = useCallback(async (): Promise<string> => {
    console.debug('[useAPI Hook] getAccessToken - START');
    if (!accessToken || !isAuthenticated) {
      console.debug('[useAPI Hook] getAccessToken - No valid token found. Fetching new token...');
      try {
        const token = await getBackendTokenWithCache();

        console.debug('[useAPI Hook] getAccessToken - New token fetched and state updated');
        setAccessToken(token);
        setIsAuthenticated(true);
        setAuthError(null);
        setUserEmail(getUserEmailFromToken(token));

        console.debug('[useAPI Hook] getAccessToken - END');
        return token;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Authentication failed';
        setAuthError(errorMessage);
        setIsAuthenticated(false);
        setUserEmail(null);
        console.error('[useAPI Hook] getAccessToken - Error:', error);
        console.debug('[useAPI Hook] getAccessToken - END (with error)');
        throw error;
      }
    }

    console.debug('[useAPI Hook] getAccessToken - Returning existing access token');
    console.debug('[useAPI Hook] getAccessToken - END');
    return accessToken;
  }, [
    accessToken,
    isAuthenticated,
    setAccessToken,
    setIsAuthenticated,
    setAuthError,
    setUserEmail,
  ]);

  const refreshToken = useCallback(async (): Promise<void> => {
    console.debug('[useAPI Hook] refreshToken - START');
    try {
      console.debug('[useAPI Hook] refreshToken - Force refreshing token...');
      const token = await getBackendTokenWithCache();

      console.debug('[useAPI Hook] refreshToken - Token refreshed and state updated');
      setAccessToken(token);
      setIsAuthenticated(true);
      setAuthError(null);
      setUserEmail(getUserEmailFromToken(token));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Token refresh failed';
      setAuthError(errorMessage);
      setIsAuthenticated(false);
      setUserEmail(null);
      console.error('[useAPI Hook] refreshToken - Error:', error);
      throw error;
    }
    console.debug('[useAPI Hook] refreshToken - END');
  }, [setAccessToken, setIsAuthenticated, setAuthError, setUserEmail]);

  const clearAuth = useCallback(() => {
    console.debug('[useAPI Hook] Clearing authentication state');
    setAccessToken(undefined);
    setIsAuthenticated(false);
    setAuthError(null);
    setLastFailingEndpoint(undefined);
    setUserEmail(null);
    localStorage.removeItem('hills_fsl_auth');
  }, [setAccessToken, setIsAuthenticated, setAuthError, setLastFailingEndpoint, setUserEmail]);

  return {
    token: accessToken,
    isAuthenticated,
    error: authError,
    isInitialized,
    getAccessToken,
    refreshToken,
    clearAuth,
  };
};
